import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, CheckCircle2, AlertCircle, Loader2, FileText, Droplets } from 'lucide-react';

import { Button }       from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label }        from '@/components/ui/label';
import { ApiErrorBanner } from '@/features/parameters/ApiErrorBanner';
import { useUploadCsv }   from './api';
import type { DataType }  from './types';

const DATA_TYPE_LABELS: Record<DataType, string> = {
  positions:   'Positions',
  accounts:    'Accounts',
  market_data: 'Market data',
};

/**
 * Phase 3.0 — CSV data-ingest page.
 *
 * Sends a multipart CSV to POST /api/v1/alm/data/upload.
 * On success the backend auto-triggers an LCR recalculation via Kafka.
 * A link to /alm/liquidity lets the analyst see the refreshed result.
 */
export function DataUploadPage() {
  const fileRef    = useRef<HTMLInputElement>(null);
  const [file,     setFile]     = useState<File | null>(null);
  const [dataType, setDataType] = useState<DataType>('positions');

  const upload = useUploadCsv();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    upload.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    upload.mutate({ file, dataType });
  }

  function handleReset() {
    setFile(null);
    upload.reset();
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Data Upload</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload a CSV file to the bronze layer. An LCR/NSFR recalculation is
          triggered automatically via Kafka once the file lands.
        </p>
      </div>

      {/* ── Upload form ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">CSV file</CardTitle>
          <CardDescription>UTF-8, comma-separated, header row required. Max 50 000 rows.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Data type selector */}
            <div className="space-y-1.5">
              <Label htmlFor="data-type">Data type</Label>
              <select
                id="data-type"
                value={dataType}
                onChange={(e) => { setDataType(e.target.value as DataType); upload.reset(); }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {(Object.entries(DATA_TYPE_LABELS) as [DataType, string][]).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* File picker */}
            <div className="space-y-1.5">
              <Label htmlFor="csv-file">File</Label>
              <label
                htmlFor="csv-file"
                className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border px-6 py-8 text-center transition hover:bg-muted/40"
              >
                {file ? (
                  <>
                    <FileText className="h-8 w-8 text-primary" />
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Click to select a .csv file
                    </span>
                  </>
                )}
              </label>
              <input
                ref={fileRef}
                id="csv-file"
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={handleFileChange}
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={!file || upload.isPending}
            >
              {upload.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" /> Upload and trigger LCR</>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* ── Error banner ─────────────────────────────────────────────── */}
      {upload.isError && <ApiErrorBanner error={upload.error} />}

      {/* ── Success card ─────────────────────────────────────────────── */}
      {upload.isSuccess && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <span className="font-medium">File accepted</span>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Batch ID</dt>
              <dd className="font-mono text-xs break-all">{upload.data.batchId}</dd>

              <dt className="text-muted-foreground">Data type</dt>
              <dd>{DATA_TYPE_LABELS[upload.data.dataType]}</dd>

              <dt className="text-muted-foreground">Rows</dt>
              <dd>{upload.data.rowCount.toLocaleString()}</dd>

              <dt className="text-muted-foreground">File</dt>
              <dd className="truncate">{upload.data.fileName}</dd>
            </dl>

            <div className="flex items-start gap-2 rounded-md bg-green-100 dark:bg-green-900 px-3 py-2 text-xs text-green-800 dark:text-green-200">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                LCR/NSFR recalculation is running in the background.
                Check the{' '}
                <Link to="/alm/liquidity" className="underline font-medium">
                  Liquidity page
                </Link>
                {' '}in a few seconds to see the updated result.
              </span>
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={handleReset}>
                Upload another file
              </Button>
              <Button size="sm" asChild>
                <Link to="/alm/liquidity">
                  <Droplets className="mr-1.5 h-3.5 w-3.5" />
                  View LCR result
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
